import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Instagram } from 'lucide-react';
import { SITE } from '@/config/site';
import { getLenis, scrollToTarget } from '@/hooks/useLenis';

const LINKS = [
  { n: '01', label: 'Inicio', href: '#top' },
  { n: '02', label: 'Mi Historia', href: '#historia' },
  { n: '03', label: 'Resultados', href: '#resultados' },
  { n: '04', label: 'El Video', href: '#video' },
  { n: '05', label: 'Testimonios', href: '#testimonios' },
  { n: '06', label: 'Productos', href: '#productos' },
];

export default function MobileNav() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 32);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    const l = getLenis();
    if (open) {
      if (l) l.stop();
      document.body.style.overflow = 'hidden';
    } else {
      if (l) l.start();
      document.body.style.overflow = '';
    }
  }, [open]);

  const go = (href) => {
    setOpen(false);
    setTimeout(() => scrollToTarget(href), 80);
  };

  return (
    <>
      <header
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-500 ${
          scrolled && !open ? 'border-b border-white/5 bg-noir/60 backdrop-blur-md' : 'border-b border-transparent bg-transparent'
        }`}
      >
        <div className="flex h-16 items-center justify-between px-5 sm:px-10">
          <button data-testid="nav-home" type="button" onClick={() => go('#top')} className="text-[11px] font-medium uppercase tracking-[0.4em] text-cream">
            Cristian Cruz<span className="text-gold">.</span>
          </button>
          <button
            data-testid="nav-toggle"
            type="button"
            aria-label={open ? 'Cerrar menú' : 'Abrir menú'}
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className="relative flex h-10 w-10 items-center justify-center"
          >
            <span className={`absolute h-px w-6 bg-cream transition-all duration-300 ${open ? 'rotate-45' : '-translate-y-1'}`} />
            <span className={`absolute h-px w-6 bg-cream transition-all duration-300 ${open ? '-rotate-45' : 'translate-y-1'}`} />
          </button>
        </div>
      </header>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-menu"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-40 flex flex-col justify-between bg-obsidian/[0.97] px-6 pb-10 pt-28 backdrop-blur-2xl sm:px-10"
          >
            <nav className="flex flex-col gap-3">
              {LINKS.map((l, i) => (
                <motion.button
                  key={l.href}
                  data-testid={`nav-link-${l.href.slice(1)}`}
                  type="button"
                  initial={{ opacity: 0, y: 26 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + i * 0.09, duration: 0.7, ease: [0.19, 1, 0.22, 1] }}
                  onClick={() => go(l.href)}
                  className="group flex items-baseline gap-4 text-left"
                >
                  <span className="text-[10px] tracking-[0.3em] text-gold">{l.n}</span>
                  <span className="font-display text-5xl uppercase leading-none text-cream transition-colors duration-300 group-hover:text-gold sm:text-6xl">
                    {l.label}
                  </span>
                </motion.button>
              ))}
            </nav>
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4, duration: 0.7 }} className="flex flex-col gap-6">
              <a
                data-testid="nav-cta-whatsapp"
                href={SITE.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center rounded-full bg-cream px-8 py-4 text-[11px] font-medium uppercase tracking-[0.25em] text-noir transition-colors duration-300 hover:bg-gold sm:self-start"
              >
                Quiero empezar
              </a>
              <a
                data-testid="nav-instagram"
                href={SITE.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-cream/50 transition-colors duration-300 hover:text-gold"
              >
                <Instagram size={13} /> {SITE.instagramHandle}
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
